/**
 * Application Routes
 * Defines the route tree using React Router data router (createBrowserRouter).
 * Enables route-level code splitting via lazy(), useBlocker, and per-route error boundaries.
 *
 * Tenant context (retailerCode + environment) lives in the Zustand store,
 * NOT in URL path segments. See design: openspec/changes/migrate-tenant-context-to-state.
 * @packageDocumentation
 */

import { useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { Navigate, useParams, useNavigate, createBrowserRouter } from 'react-router';

import { useEnvironments } from '@/hooks';
import { RootShell } from '@/components/RootShell';
import { AppLoader, EmptyState } from '@/components/ui';
import { EnvironmentLayout } from '@/components/EnvironmentLayout';
import { RouteErrorBoundary } from '@/components/ui/RouteErrorBoundary';

/**
 * Full page loading fallback for auth pages.
 *
 * @returns { JSX.Element } The full page loading component.
 */
const FullPageLoader = () => <AppLoader />;

/**
 * Full-page message shown when the user has no Nexus-enabled retailers.
 *
 * @returns { JSX.Element } The empty state page.
 */
const NoNexusRetailersPage = () => (
  <div className="flex min-h-screen items-center justify-center bg-background p-6">
    <EmptyState
      variant="featured"
      icon={<Building2 size={32} className="text-muted-foreground" />}
      title="No Nexus retailers configured"
      description="None of your accessible retailers have Nexus enabled. To get started, enable Nexus for a retailer in LDA and ensure your account has access."
    />
  </div>
);

/**
 * Store-aware root redirect. Waits for retailers to load, auto-selects
 * the first retailer/environment if the store is empty, then redirects to /dashboard.
 *
 * @returns { JSX.Element } A redirect, loader, or empty state.
 */
const StoreRedirect = () => {
  const { retailers, isLoading } = useEnvironments();

  if (isLoading) {
    return <AppLoader />;
  }

  if (retailers.length === 0 || !retailers.some((r) => r.environments.length > 0)) {
    return <NoNexusRetailersPage />;
  }

  // Store auto-selects the first retailer in setRetailers() when no valid selection exists
  return <Navigate to="/dashboard" replace />;
};

/**
 * Legacy URL redirect for bookmarks/links from before the tenant-context migration.
 * Matches `/:segment1/:segment2/*`, applies segments as retailer/environment
 * to the store if valid, then redirects to the remainder path.
 *
 * @returns { JSX.Element } A redirect to the clean path.
 */
const LegacyTenantRedirect = () => {
  const navigate = useNavigate();
  const { segment1, segment2, '*': rest } = useParams<{ '*': string; segment1: string; segment2: string }>();
  const { retailers, isLoading, selectEnvironment } = useEnvironments();

  useEffect(() => {
    if (isLoading || !segment1 || !segment2) {
      return;
    }

    const retailer = retailers.find((r) => r.retailerCode === segment1);

    if (retailer) {
      const env = retailer.environments.find((e) => e.environment === segment2);

      if (env) {
        selectEnvironment(segment1, env.environment);
      }

      navigate(`/${rest || 'dashboard'}`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, segment1, segment2, rest, retailers, selectEnvironment, navigate]);

  return <AppLoader />;
};

// #region Page Routes

/**
 * Page route definitions. Each page is lazy-loaded for code splitting.
 * Paths are flat — no tenant prefix.
 */
const pageRoutes = [
  {
    path: 'dashboard',
    lazy: () => import('@/pages/DashboardPage').then((m) => ({ Component: m.DashboardPage })),
  },
  {
    path: 'mappings',
    lazy: () => import('@/pages/MappingsPage').then((m) => ({ Component: m.MappingsPage })),
  },
  {
    path: 'mappings/new',
    lazy: () => import('@/pages/MappingEditorPage').then((m) => ({ Component: m.MappingEditorPage })),
  },
  {
    path: 'mappings/:id',
    lazy: () => import('@/pages/MappingEditorPage').then((m) => ({ Component: m.MappingEditorPage })),
  },
  {
    path: 'dlq',
    lazy: () => import('@/pages/DlqPage').then((m) => ({ Component: m.DlqPage })),
  },
  {
    path: 'dlq/:type',
    lazy: () => import('@/pages/DlqPage').then((m) => ({ Component: m.DlqPage })),
  },
];

// #endregion

/**
 * Application router created with createBrowserRouter.
 * Supports useBlocker, route-level error boundaries, and lazy loading.
 */
export const router = createBrowserRouter([
  // Public auth routes
  {
    path: '/login',
    HydrateFallback: FullPageLoader,
    lazy: () => import('@/pages/LoginPage').then((m) => ({ Component: m.LoginPage })),
  },
  {
    path: '/forgot-password',
    HydrateFallback: FullPageLoader,
    lazy: () => import('@/pages/ForgotPasswordPage').then((m) => ({ Component: m.ForgotPasswordPage })),
  },
  {
    path: '/unauthorized',
    HydrateFallback: FullPageLoader,
    lazy: () => import('@/pages/UnauthorizedPage').then((m) => ({ Component: m.UnauthorizedPage })),
  },

  // Standalone pipeline log viewer — opens in a browser popup window (window.open)
  // with its own WebSocket connection. No auth shell needed since the WS
  // endpoint validates the session cookie independently.
  {
    path: '/pipeline-logs',
    HydrateFallback: FullPageLoader,
    lazy: () => import('@/pages/PipelineLogsPage').then((m) => ({ Component: m.PipelineLogsPage })),
  },

  // Authenticated routes — flat route tree, tenant context from Zustand store
  {
    path: '/',
    element: <RootShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <EnvironmentLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          // Root index — ensures store has a retailer before showing dashboard
          { index: true, element: <StoreRedirect /> },
          ...pageRoutes,
          // Catch-all → dashboard
          { path: '*', element: <Navigate to="/dashboard" replace /> },
        ],
      },
      // Legacy tenant-prefixed URLs (e.g. /retailerCode/environment/mappings).
      // React Router scores static segments (3 pts) above dynamic params (2 pts),
      // so all existing page routes inside EnvironmentLayout outscore this catch-all.
      // Any future two-segment route added as a direct child of RootShell must also
      // outscore this pattern — place it inside EnvironmentLayout or use a static prefix.
      // TODO: Remove after 2026-Q3 when legacy bookmarks have aged out
      { path: ':segment1/:segment2/*', element: <LegacyTenantRedirect /> },
    ],
  },
]);
